import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { ArrowLeft, CreditCard, Zap, Lock } from "lucide-react";
import { api, ApiError, apiErrorCode, formatMoney } from "../lib/api.js";
import { checkoutErrorMessage, checkoutErrorDetail, isPixUnavailableError, logApiError } from "../lib/apiErrors.js";
import { captureAndGetUTMs } from "../lib/utm.js";
import { metaEvent } from "../lib/metaPixel.js";
import { PhoneInput } from "../components/PhoneInput.js";
import { CpfInput } from "../components/CpfInput.js";
import { CouponSection } from "../components/CouponSection.js";
import {
  bumpComboState,
  OrderBumpSection,
  OrderBumpSectionLoading,
  type OrderBumpItem,
} from "../components/OrderBumpSection.js";
import { PaymentStickyBar } from "../components/PaymentStickyBar.js";
import { ResumePixBanner } from "../components/ResumePixBanner.js";
import { StoneCardForm } from "../components/StoneCardForm.js";
import { RenewalTerms } from "../components/RenewalTerms.js";
import { EMPTY_STONE_CARD, invalidStoneCardField, tokenizeStoneCard, type StoneCardFields } from "../lib/stoneCard.js";
import type { Plan, BillingInterval, PaymentMethod, PricingBreakdown } from "../../../shared/types.js";
import { PLANS, getAnnualSavings, currencyFromLocale, isPixAvailableForLocale, getPlanPricing } from "../../../shared/plans.js";
import { preselectRequestedBumps, requestedBumpKeys } from "../../../shared/order_bumps.js";
import {
  formatCPF,
  isValidCPF,
  isValidFullName,
  isLikelyValidEmail,
  normalizeEmail,
  suggestEmailFix,
} from "../../../shared/validation.js";
import {
  isPixAvailableForPhone,
  isValidPhoneE164,
  isValidPhoneForCountry,
  needsCpfAtCheckout,
  parsePhone,
  toE164,
} from "../../../shared/phone.js";
import {
  ensureActivePixOrder,
  getStoredActivePixOrderId,
  isPixOrderActive,
  loadPixCheckoutContext,
  pixOrderMatchesBumps,
  savePixCheckoutContext,
  type PixOrderSnapshot,
} from "../lib/pixCheckout.js";
import { saveStripeCheckoutContext, saveStripeCheckoutSession } from "../lib/stripeCheckout.js";
import { localizedInputValidationProps } from "../lib/localizedValidation.js";
import { isCheckoutOfferKind, type CheckoutOfferKind } from "../../../shared/checkout_offers.js";
import { CheckoutOfferBanner } from "../components/CheckoutOfferBanner.js";
import {
  CHECKOUT_FIELD_IDS,
  CHECKOUT_FIELD_MESSAGE_KEYS,
  checkoutFieldErrorFromApiCode,
  checkoutInvalidEventName,
  nativeInvalidEventFor,
  revealCheckoutField,
  validateCheckoutFields,
  type CheckoutField,
  type CheckoutFieldError,
} from "../lib/checkoutValidation.js";
import { clarityEvent } from "../lib/clarity.js";
import { copyNavigationContext } from "../lib/plansNavigation.js";
import {
  CHECKOUT_FORM_MILESTONES_S,
  checkoutFormMilestoneEventName,
  createVisibleTimeTracker,
} from "../lib/visibleTimeMilestones.js";
import { persistCheckoutOfferToken, resolveCheckoutOfferToken, offerFailureI18nKey } from "../lib/checkoutOfferSession.js";
import { cleanSalesToken } from "../../../shared/sales_token.js";
import { STONE_INSTALLMENT_COUNT } from "../../../shared/stone.js";

// Terms URLs — kept for when the consent checkbox is re-enabled (see below).
// const TERMS_URL = "https://zenpost.ai/termos-de-uso/";
// const REFUND_URL = "https://zenpost.ai/reembolso-e-garantias/";

/** Where existing subscribers manage billing inside app.zenpost.ai. */
const ZENPOST_APP_SUBSCRIPTION_URL = "https://app.zenpost.ai/more/subscription";

/**
 * Step 2 of the checkout flow.
 *
 * Stripe-style two-column layout:
 *   - Left:  order summary, "switch to annual" upsell, total
 *   - Right: payment-method tabs, minimal customer details, inline coupon,
 *            single primary "Pay R$ XX" button
 *
 * Expects ?plan= and ?interval= in the URL. If missing, redirects to /plans.
 * Full name is collected (required) and forwarded to Stripe/OpenPix and n8n.
 * Terms checkbox was removed per UX requirements; see commit log.
 */
export function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const [, setLocation] = useLocation();

  // --- URL → initial state --------------------------------------------------
  // Supported query params:
  //   plan, interval, method            ← preselects pricing
  //   email, whatsapp (or phone), cpf   ← prefill customer fields
  //   country                            ← ISO-2 to seed the phone-country picker (defaults to BR)
  // Example: /checkout?plan=premium&interval=annual&whatsapp=%2B5511999999999
  const search = typeof window !== "undefined" ? window.location.search : "";
  const urlParams = new URLSearchParams(search);
  const urlPlan = (urlParams.get("plan") as Plan) || null;
  const urlInterval = (urlParams.get("interval") as BillingInterval) || null;
  const urlMethod = (urlParams.get("method") as PaymentMethod) || null;
  const urlEmail = urlParams.get("email") ?? "";
  const urlName = urlParams.get("name") ?? "";
  const urlWhatsapp = urlParams.get("whatsapp") ?? urlParams.get("phone") ?? "";
  const urlCpf = urlParams.get("cpf") ?? "";
  const urlCountry = urlParams.get("country") ?? undefined;
  const urlPixOrder = urlParams.get("pix_order") ?? "";
  // Sales/preview token forwarded from zenpost-frontend — used to prefill the
  // phone number from the lead behind the token (first-purchase convenience).
  const urlSalesToken = cleanSalesToken(urlParams.get("sales_token"));
  // Explicit "the phone is locked" flag — persists the lock across internal
  // navigations (back to plans, resume PIX, Stripe back) where the async
  // sales-lead lookup would otherwise not re-run.
  const urlPhoneLocked = urlParams.get("phone_locked") === "1";
  const urlOfferFromQuery = urlParams.get("offer") ?? "";
  const urlOfferToken = resolveCheckoutOfferToken(urlOfferFromQuery);
  // Add-ons the link asks to open ticked (`?bump=zenlinks`, from the zenpost /previews
  // "Quero minha página" button). Keyed on the parameter alone: the page rewrites its own URL (plan,
  // period, an invalid offer dropped), and each rewrite would fetch the add-on list again.
  const urlBumpParam = new URLSearchParams(search).get("bump");
  const urlBumps = useMemo(() => requestedBumpKeys(urlBumpParam), [urlBumpParam]);
  // Ticked once, the first time the add-on list sells one of them; after that the buyer's
  // choice stands, so a refetch (the phone typed, the period switched) never ticks it again.
  const bumpPreselectPending = useRef(urlBumps.length > 0);

  useEffect(() => {
    const params = new URLSearchParams(search);
    let needsNav = false;
    if (urlOfferToken && !urlOfferFromQuery) {
      params.set("offer", urlOfferToken);
      needsNav = true;
    }
    if (urlOfferToken && params.get("interval") === "annual") {
      params.set("interval", "monthly");
      needsNav = true;
    }
    if (urlPlan === "basic" || urlPlan === "premium") {
      if (needsNav) setLocation(`/checkout?${params.toString()}`);
      return;
    }
    if (urlOfferToken) {
      params.set("plan", params.get("plan") === "basic" ? "basic" : "premium");
      params.set("interval", "monthly");
      params.set("offer", urlOfferToken);
      setLocation(`/checkout?${params.toString()}`);
      return;
    }
    setLocation(`/plans${params.toString() ? `?${params.toString()}` : ""}`);
  }, [urlPlan, urlOfferToken, urlOfferFromQuery, search, setLocation]);

  const [plan] = useState<Plan>(urlPlan ?? "premium");
  const [interval, setIntervalValue] = useState<BillingInterval>(urlOfferToken ? "monthly" : (urlInterval ?? "monthly"));
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(() => {
    if (urlMethod) return urlMethod;
    return isPixAvailableForLocale(i18n.language) ? "openpix" : "stripe";
  });
  const currency = currencyFromLocale(i18n.language);
  const money = (cents: number) => formatMoney(cents, i18n.language, currency);

  // --- Customer fields ------------------------------------------------------
  // `phone` is the full E.164 string ("+5511999999999"); the PhoneInput
  // component owns the country selector and gives us back the final string.
  const [fullName, setFullName] = useState(urlName);
  const [email, setEmail] = useState(() => normalizeEmail(urlEmail));
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const initialPhone = useMemo(() => {
    if (!urlWhatsapp) return "";
    const parsed = parsePhone(urlWhatsapp, urlCountry ?? "BR");
    return toE164(parsed.country, parsed.local);
  }, [urlWhatsapp, urlCountry]);
  const [phone, setPhone] = useState(initialPhone);
  const [cpf, setCpf] = useState(() => formatCPF(urlCpf));
  // When the phone is resolved from the sales/preview token it is the lead's
  // WhatsApp identifier used for reconciliation, so we lock the field. The lock
  // is sticky across internal navigations via the explicit `phone_locked` flag.
  const [phoneLockedFromToken, setPhoneLockedFromToken] = useState(urlPhoneLocked);

  // Prefill the phone from the sales/preview token. Locks the field even when a
  // phone is already present (e.g. `whatsapp=` was forwarded on a round-trip);
  // it never overwrites a value the customer has already typed.
  useEffect(() => {
    if (!urlSalesToken) return;
    let cancelled = false;
    void (async () => {
      try {
        const lead = await api<{ found: boolean; phone_number?: string | null }>(
          `/api/public/sales-lead?token=${encodeURIComponent(urlSalesToken)}`,
        );
        if (cancelled || !lead.found || !lead.phone_number) return;
        const parsed = parsePhone(lead.phone_number, urlCountry ?? "BR");
        const e164 = toE164(parsed.country, parsed.local);
        if (!e164) return;
        setPhone((prev) => prev || e164);
        setPhoneLockedFromToken(true);
      } catch {
        // Soft-fail: the customer can still enter the phone manually.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [urlSalesToken, urlCountry]);

  const needsCpf = needsCpfAtCheckout(i18n.language, phone, urlCountry ?? "BR");
  const pixLocaleAvailable = isPixAvailableForLocale(i18n.language);
  const pixAvailable =
    pixLocaleAvailable && isPixAvailableForPhone(phone, urlCountry ?? "BR");

  // Card-only locales hide PIX; non-BR phones disable PIX but keep the tabs.
  useEffect(() => {
    if (!pixAvailable && paymentMethod === "openpix") {
      setPaymentMethod("stripe");
    }
  }, [pixAvailable, paymentMethod]);

  // --- Coupon (inline-collapsible, Stripe-style) ----------------------------
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponDraft, setCouponDraft] = useState("");
  const [couponApplied, setCouponApplied] = useState<string>("");
  const [couponError, setCouponError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The error shown is Woovi failing to create the PIX: it comes with a button to pay by card. */
  const [pixUnavailable, setPixUnavailable] = useState(false);
  // A field zenpay refused: the message sits under that field instead of at the end of the form.
  const [fieldError, setFieldError] = useState<CheckoutFieldError | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [emailAlreadyActive, setEmailAlreadyActive] = useState(false);
  const [breakdown, setBreakdown] = useState<PricingBreakdown | null>(null);
  const [stonePublicKey, setStonePublicKey] = useState<string | null>(null);
  const [stoneCard, setStoneCard] = useState<StoneCardFields>(EMPTY_STONE_CARD);
  const [activePixOrder, setActivePixOrder] = useState<PixOrderSnapshot | null>(null);
  const [bumpItems, setBumpItems] = useState<OrderBumpItem[]>([]);
  // True while the add-on list for this phone is being fetched (the sales bot
  // answer can take a second): the section shows a placeholder instead of
  // appearing late under a buyer who already scrolled to "Pagar".
  const [bumpsLoading, setBumpsLoading] = useState(false);
  const [selectedBumpKeys, setSelectedBumpKeys] = useState<Set<string>>(() => new Set());
  const [bumpPaidCents, setBumpPaidCents] = useState(0);
  const [pricingBumpLines, setPricingBumpLines] = useState<
    { key: string; headline: string; list_cents: number; charged_cents: number; source: string }[]
  >([]);
  // Offer links sell add-ons too, on top of the offer at their full price.
  const bumpsEnabled = currency === "BRL";
  const [offerInvalidReason, setOfferInvalidReason] = useState<string | null>(null);

  const utms = useMemo(() => captureAndGetUTMs(), []);
  const def = PLANS[plan];
  const annualSavings = getAnnualSavings(plan, currency);

  // --- Order bumps (BRL) ----------------------------------------------------
  // Fetched as soon as the page opens (all for sale) and again once the phone is
  // valid, when the bot's promises turn into gifts. Keyed on the valid phone only,
  // so typing it digit by digit does not refetch.
  const bumpPhone = useMemo(() => {
    if (!isValidPhoneE164(phone)) return "";
    // Only a complete number for its country: an 8-digit prefix of a Brazilian
    // mobile would otherwise ask the sales bot once per typed digit.
    const parsed = parsePhone(phone, urlCountry ?? "BR");
    return isValidPhoneForCountry(parsed.country, parsed.local) ? phone : "";
  }, [phone, urlCountry]);
  useEffect(() => {
    if (!bumpsEnabled) {
      setBumpItems([]);
      // Same Set when nothing was selected, so typing the phone does not re-price.
      setSelectedBumpKeys((prev) => (prev.size === 0 ? prev : new Set()));
      setBumpPaidCents(0);
      setPricingBumpLines([]);
      setBumpsLoading(false);
      return;
    }
    let cancelled = false;
    setBumpsLoading(true);
    void api<{ show: boolean; items: OrderBumpItem[] }>("/api/public/order-bumps", {
      method: "POST",
      body: JSON.stringify({ phone: bumpPhone, interval, app_language: i18n.language }),
    })
      .then((r) => {
        if (cancelled) return;
        const items = r.show ? r.items : [];
        setBumpItems(items);
        // Switching monthly/annual only re-prices the add-ons: keep what the buyer
        // picked, minus anything no longer sold or now included as a gift.
        const offered = new Set(items.filter((i) => i.kind === "offer").map((i) => i.key));
        const preselect =
          bumpPreselectPending.current && urlBumps.some((key) => offered.has(key)) ? urlBumps : [];
        if (preselect.length) bumpPreselectPending.current = false;
        setSelectedBumpKeys((prev) => {
          const kept = [...prev].filter((key) => offered.has(key));
          const base = kept.length === prev.size ? prev : new Set(kept);
          return preselectRequestedBumps(base, items, preselect);
        });
      })
      .catch(() => {
        if (cancelled) return;
        // Nothing is shown, so nothing hidden may stay selected (and charged).
        setBumpItems([]);
        setSelectedBumpKeys((prev) => (prev.size === 0 ? prev : new Set()));
      })
      .finally(() => {
        if (!cancelled) setBumpsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bumpPhone, interval, i18n.language, bumpsEnabled, urlBumps]);

  const bumpCombo = bumpComboState(bumpItems, selectedBumpKeys, interval);

  // --- Re-price whenever something relevant changes ------------------------
  useEffect(() => {
    let cancelled = false;
    const pricingBody: Record<string, unknown> = {
      plan,
      interval,
      payment_method: paymentMethod,
      coupon_code: couponApplied || undefined,
      app_language: i18n.language,
      selected_bump_keys: [...selectedBumpKeys],
      bump_combo: bumpCombo.active || undefined,
      offer_token: urlOfferToken || undefined,
    };
    api<{
      breakdown: PricingBreakdown;
      coupon_error: string | null;
      bump_paid_cents?: number;
      bump_lines?: {
        key: string;
        headline: string;
        list_cents: number;
        charged_cents: number;
        source: string;
      }[];
      total_cents?: number;
      card_provider?: "stripe" | "stone" | null;
      stone_public_key?: string | null;
    }>("/api/public/pricing", {
      method: "POST",
      body: JSON.stringify(pricingBody),
    })
      .then((r) => {
        if (cancelled) return;
        if (urlOfferToken) setOfferInvalidReason(null);
        setBreakdown(r.breakdown);
        setStonePublicKey(r.card_provider === "stone" ? (r.stone_public_key ?? null) : null);
        setBumpPaidCents(r.bump_paid_cents ?? 0);
        setPricingBumpLines(r.bump_lines ?? []);
        if (r.coupon_error === "not_found" && couponApplied) {
          setCouponError("not_found");
          setCouponApplied("");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (!urlOfferToken) return;
        if (!(err instanceof ApiError) || err.status !== 400) return;
        setOfferInvalidReason(apiErrorCode(err) ?? "not_found");
        persistCheckoutOfferToken(null);
        const params = new URLSearchParams(search);
        if (!params.get("offer")) return;
        params.delete("offer");
        setLocation(`/checkout?${params.toString()}`);
      });
    return () => {
      cancelled = true;
    };
  }, [
    plan,
    interval,
    paymentMethod,
    couponApplied,
    i18n.language,
    selectedBumpKeys,
    bumpCombo.active,
    urlOfferToken,
  ]);

  // If the customer stepped back from the PIX screen, offer to resume the same charge.
  useEffect(() => {
    const candidateId = urlPixOrder || getStoredActivePixOrderId();
    if (!candidateId) return;

    const storedCtx = loadPixCheckoutContext(candidateId);
    if (storedCtx?.fullName && !urlName) setFullName(storedCtx.fullName);
    if (storedCtx?.phone && !urlWhatsapp) setPhone(storedCtx.phone);
    if (storedCtx?.cpf && !urlCpf) setCpf(formatCPF(storedCtx.cpf));

    let cancelled = false;
    void (async () => {
      try {
        const order = await api<PixOrderSnapshot>(`/api/public/orders/${candidateId}`);
        if (cancelled) return;
        if (order.plan !== plan || order.interval !== interval) return;

        const orderEmail = (order.email ?? storedCtx?.email ?? "").trim().toLowerCase();
        const currentEmail = email.trim().toLowerCase();
        if (currentEmail && orderEmail && currentEmail !== orderEmail) {
          setActivePixOrder(null);
          return;
        }

        const returningFromPix = Boolean(urlPixOrder) || urlMethod === "openpix";

        if (!isPixOrderActive(order)) {
          const refreshed = await ensureActivePixOrder(candidateId);
          if (cancelled) return;
          setActivePixOrder(refreshed);
          setSelectedBumpKeys(new Set(refreshed.bump_keys ?? order.bump_keys ?? []));
          if (returningFromPix) setPaymentMethod("openpix");
          if (refreshed.email && !email) setEmail(refreshed.email);
          return;
        }
        setActivePixOrder(order);
        // Back from the PIX screen: restore the add-ons that charge was for.
        setSelectedBumpKeys(new Set(order.bump_keys ?? []));
        if (returningFromPix) setPaymentMethod("openpix");
        if (order.email && !email) setEmail(order.email);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally omit `email` — re-running on every keystroke was forcing PIX again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPixOrder, plan, interval, urlWhatsapp, urlCpf, urlName, urlMethod]);

  // Hide the PIX resume banner when the customer edits the email away from that charge.
  useEffect(() => {
    if (!activePixOrder?.email) return;
    const orderEmail = activePixOrder.email.trim().toLowerCase();
    const currentEmail = email.trim().toLowerCase();
    if (currentEmail && orderEmail && currentEmail !== orderEmail) {
      setActivePixOrder(null);
    }
  }, [email, activePixOrder?.email, activePixOrder?.id]);

  // --- Coupon actions -------------------------------------------------------
  async function applyCoupon() {
    setCouponError(null);
    const code = couponDraft.trim().toUpperCase();
    if (!code) return;
    try {
      const r = await api<{ breakdown: PricingBreakdown; coupon_error: string | null }>("/api/public/pricing", {
        method: "POST",
        body: JSON.stringify({
          plan,
          interval,
          payment_method: paymentMethod,
          coupon_code: code,
        }),
      });
      if (r.coupon_error === "not_found") {
        setCouponError("not_found");
        return;
      }
      setCouponApplied(code);
      setBreakdown(r.breakdown);
      setCouponDraft("");
    } catch {
      setCouponError("not_found");
    }
  }
  function removeCoupon() {
    setCouponApplied("");
    setCouponDraft("");
    setCouponError(null);
  }

  // --- Checkout opened (Meta) ----------------------------------------------------
  // Once per page, when the first price is known, so Meta learns from people who reach checkout
  // and not only from the few who pay. Plan and interval only; nothing the buyer typed.
  const initiateCheckoutSent = useRef(false);
  useEffect(() => {
    if (!breakdown || initiateCheckoutSent.current) return;
    initiateCheckoutSent.current = true;
    metaEvent("InitiateCheckout", { valueCents: breakdown.finalCents, currency, contentName: `${plan}_${interval}` });
  }, [breakdown, currency, plan, interval]);

  // --- Time with the form on screen (Clarity) -------------------------------
  // The form renders on the first paint, so the clock starts on mount and stops when the buyer
  // leaves for the payment step. Hidden-tab time does not count.
  useEffect(() => {
    const tracker = createVisibleTimeTracker({
      milestonesS: CHECKOUT_FORM_MILESTONES_S,
      onMilestone: (seconds) => clarityEvent(checkoutFormMilestoneEventName(seconds)),
    });
    const tick = () => tracker.tick(performance.now(), document.visibilityState === "visible");
    tick();
    const interval = window.setInterval(tick, 1000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  // Refusals that belong to no field (email already subscribed, card provider down) render at the
  // end of the form, below the fold on a phone whose Pay button is the fixed bar.
  const formErrorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!error && !emailAlreadyActive) return;
    formErrorRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [error, emailAlreadyActive]);

  function clearFieldError(field: CheckoutField) {
    setFieldError((current) => (current?.field === field ? null : current));
  }

  function barPay(invalid: CheckoutFieldError) {
    setFieldError(invalid);
    if (invalid.suggestion) setEmailSuggestion(invalid.suggestion);
    clarityEvent(checkoutInvalidEventName(invalid.code));
    revealCheckoutField(invalid.field);
  }

  function fieldErrorMessage(field: CheckoutField): string | null {
    if (fieldError?.field !== field) return null;
    // The typo hint under the email already asks "did you mean …?" with the fix as a button.
    if (fieldError.code === "email_typo") return null;
    return t(CHECKOUT_FIELD_MESSAGE_KEYS[fieldError.code]);
  }

  // --- Submit ---------------------------------------------------------------
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPixUnavailable(false);
    setFieldError(null);
    setErrorDetail(null);
    setEmailAlreadyActive(false);
    const normalizedEmail = normalizeEmail(email);
    if (normalizedEmail !== email) setEmail(normalizedEmail);
    const invalid = validateCheckoutFields({ fullName, email: normalizedEmail, phone, cpf, needsCpf });
    if (invalid) return barPay(invalid);
    if (payWithStone) {
      const badField = invalidStoneCardField(stoneCard);
      if (badField) return setError(t(`checkout.stone.errors.${badField}`));
    }

    setSubmitting(true);
    try {
      let cardToken: string | undefined;
      if (payWithStone && stonePublicKey) {
        try {
          cardToken = await tokenizeStoneCard(stonePublicKey, stoneCard);
        } catch (tokenErr) {
          logApiError("checkout.stone.tokenize", tokenErr);
          setError(t("checkout.stone.errors.tokenize"));
          setSubmitting(false);
          return;
        }
      }

      // Reuse the same PIX charge while it is still valid — avoid duplicate orders.
      if (paymentMethod === "openpix" && activePixOrder) {
        if (
          activePixOrder.plan === plan &&
          activePixOrder.interval === interval &&
          pixOrderMatchesBumps(activePixOrder, selectedBumpKeys) &&
          (!activePixOrder.email || activePixOrder.email.toLowerCase() === normalizedEmail)
        ) {
          setLocation(`/pix/pending/${activePixOrder.id}`);
          return;
        }
      }

      const res = await api<{
        order_id: string;
        final_cents?: number;
        stripe_client_secret?: string;
        stripe_publishable_key?: string | null;
        pix_emv?: string;
        recurring_cents?: number;
        first_charge_at?: string | null;
        payment_method?: PaymentMethod;
        charge_status?: "paid" | "pending" | "failed" | null;
      }>("/api/public/checkout", {
        method: "POST",
        body: JSON.stringify({
          email: normalizedEmail,
          full_name: fullName.trim(),
          phone,
          cpf: needsCpf ? cpf : undefined,
          plan,
          interval,
          payment_method: paymentMethod,
          coupon_code: urlOfferToken ? undefined : couponApplied || undefined,
          offer_token: urlOfferToken || undefined,
          app_language: i18n.language,
          // Terms checkbox is currently commented out in the UI; we still
          // send `accept_terms: true` because the backend treats it as a hard
          // gate. Re-enabling the checkbox is a single uncomment below.
          accept_terms: true,
          selected_bump_keys: [...selectedBumpKeys],
          // What this page showed: a returning customer loses only the bonus of a paid add-on that was
          // here to tick again (none while the list is loading or failed to load).
          offered_bump_keys: bumpItems.map((item) => item.key),
          bump_combo: bumpCombo.active || undefined,
          ...(payWithStone ? { card_token: cardToken } : {}),
          // An iPad in desktop mode sends a Mac User-Agent; its touch screen is what gives it away.
          apple_touch: typeof navigator !== "undefined" && navigator.maxTouchPoints > 1,
          // The preview or bot link this checkout came from: the order keeps it (PIT-485 e).
          sales_token: urlSalesToken || undefined,
          ...utms,
        }),
      });
      // A PIX or card charge now exists. The order id is the event id, so a server-side copy can
      // later be deduplicated by Meta.
      metaEvent(
        "AddPaymentInfo",
        {
          valueCents: res.final_cents ?? checkoutTotalCents,
          currency,
          contentName: `${plan}_${interval}`,
          paymentType: res.payment_method ?? paymentMethod,
        },
        res.order_id,
      );
      if (res.payment_method === "stone") {
        // Charged during the call (or held for review): the success page polls the order.
        setStoneCard(EMPTY_STONE_CARD);
        setLocation(`/success?order=${res.order_id}`);
        return;
      }
      if (paymentMethod === "stripe" && res.stripe_client_secret) {
        const publishableKey = res.stripe_publishable_key ?? "";
        try {
          saveStripeCheckoutContext({
            orderId: res.order_id,
            plan,
            interval,
            email: normalizedEmail,
            fullName: fullName.trim() || undefined,
            phone,
            cpf: needsCpf ? cpf.replace(/\D/g, "") : undefined,
            salesToken: urlSalesToken || undefined,
            phoneLocked: phoneLockedFromToken || undefined,
            offerToken: urlOfferToken || undefined,
          });
          if (publishableKey) {
            saveStripeCheckoutSession(res.order_id, {
              clientSecret: res.stripe_client_secret,
              publishableKey,
              finalCents: res.final_cents ?? breakdown?.finalCents ?? 0,
              renewalCents: res.recurring_cents,
              interval,
              firstChargeAt: res.first_charge_at ?? null,
            });
          }
        } catch (storageErr) {
          logApiError("checkout.stripe.sessionStorage", storageErr);
        }
        setLocation(`/stripe/checkout/${res.order_id}`);
        return;
      } else if (paymentMethod === "stripe") {
        setError(t("stripeCheckout.notConfigured"));
        setSubmitting(false);
      } else if (paymentMethod === "openpix") {
        if (res.pix_emv) {
          sessionStorage.setItem(`pix_emv_${res.order_id}`, res.pix_emv);
        }
        savePixCheckoutContext({
          orderId: res.order_id,
          plan,
          interval,
          email: normalizedEmail,
          fullName: fullName.trim() || undefined,
          phone,
          cpf: needsCpf ? cpf.replace(/\D/g, "") : undefined,
          salesToken: urlSalesToken || undefined,
          phoneLocked: phoneLockedFromToken || undefined,
          offerToken: urlOfferToken || undefined,
        });
        setLocation(`/pix/pending/${res.order_id}`);
      } else {
        setLocation(`/success?order=${res.order_id}`);
      }
    } catch (err) {
      logApiError("checkout.submit", err);
      const refusedField = err instanceof ApiError ? checkoutFieldErrorFromApiCode(apiErrorCode(err)) : null;
      const inFlightOrderId =
        err instanceof ApiError && apiErrorCode(err) === "stone_checkout_in_progress"
          ? (err.body as { order_id?: string } | null)?.order_id
          : undefined;
      if (inFlightOrderId) {
        // Another submit of this buyer is already charging the card: wait for that one.
        setStoneCard(EMPTY_STONE_CARD);
        setLocation(`/success?order=${inFlightOrderId}`);
        return;
      }
      if (err instanceof ApiError && err.status === 409) {
        setEmailAlreadyActive(true);
        clarityEvent("checkout_email_already_active");
      } else if (refusedField) {
        barPay(refusedField);
      } else if (err instanceof ApiError && err.status === 402 && apiErrorCode(err) === "card_declined") {
        // A declined card can be retried with the same or another card.
        setStoneCard((c) => ({ ...c, cvv: "" }));
        setError(t("checkout.stone.errors.declined"));
      } else if (err instanceof ApiError && apiErrorCode(err) === "stone_checkout_failed") {
        setError(t("checkout.stone.errors.failed"));
      } else if (
        err instanceof ApiError &&
        (apiErrorCode(err) === "too_many_attempts" || (payWithStone && apiErrorCode(err) === "rate_limit_exceeded"))
      ) {
        setStoneCard(EMPTY_STONE_CARD);
        setError(t("checkout.stone.errors.tooManyAttempts"));
      } else {
        clarityEvent("checkout_submit_failed");
        setError(checkoutErrorMessage(err, paymentMethod, t));
        setPixUnavailable(paymentMethod === "openpix" && isPixUnavailableError(err));
        setErrorDetail(checkoutErrorDetail(err));
      }
      setSubmitting(false);
    }
  }

  const phoneField = (
    <Field
      label={t("checkout.phone")}
      error={fieldErrorMessage("phone")}
      badge={
        phoneLockedFromToken ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-purple/10 px-2 py-0.5 text-[11px] font-medium text-purple-deep">
            <Lock className="w-3 h-3" aria-hidden="true" />
            {t("checkout.phoneConfirmed")}
          </span>
        ) : undefined
      }
    >
      <PhoneInput
        id={CHECKOUT_FIELD_IDS.phone}
        value={phone}
        onChange={(next) => {
          setPhone(next);
          clearFieldError("phone");
        }}
        defaultCountryIso={urlCountry ?? "BR"}
        autoComplete="tel"
        required
        disabled={phoneLockedFromToken}
      />
      {phoneLockedFromToken && (
        <p className="mt-1.5 text-xs text-ink-2">{t("checkout.phoneLocked")}</p>
      )}
    </Field>
  );

  const checkoutTotalCents = breakdown ? breakdown.finalCents + bumpPaidCents : null;
  // Same rule as the server: annual card in BRL, and a CPF to send Stone.
  const payWithStone = Boolean(stonePublicKey) && paymentMethod === "stripe" && interval === "annual" && needsCpf;
  const stoneInstallmentsLabel =
    payWithStone && breakdown?.installments && breakdown.installmentCents
      ? t("plans.annualInstallments", { count: breakdown.installments, value: money(breakdown.installmentCents) })
      : null;

  // Gifts cost nothing, so pricing does not return them — the summary lists
  // them next to the paid add-ons so the buyer sees everything they get.
  const summaryBumpLines = useMemo(
    () => [
      // The combo is one price for all of them, so it is one line.
      ...(bumpCombo.active && pricingBumpLines.length > 1
        ? [
            {
              key: "combo",
              headline: t("checkout.bumps.comboSummary", { count: pricingBumpLines.length }),
              list_cents: pricingBumpLines.reduce((sum, line) => sum + line.list_cents, 0),
              charged_cents: pricingBumpLines.reduce((sum, line) => sum + line.charged_cents, 0),
              source: "selected",
            },
          ]
        : pricingBumpLines),
      ...bumpItems
        .filter((i) => i.kind === "gift")
        .map((i) => ({
          key: i.key,
          headline: i.headline,
          list_cents: i.list_cents,
          charged_cents: 0,
          source: "gift",
        })),
    ],
    [pricingBumpLines, bumpItems, bumpCombo.active, t],
  );
  const payAmountLabel = checkoutTotalCents != null ? money(checkoutTotalCents) : "—";
  // Annual on the card is 12 interest-free instalments: the button says so
  // ("Pagar 12x de R$ 35,00") instead of the year's total.
  const installmentsPayLabel =
    payWithStone && breakdown?.installments && breakdown.installmentCents
      ? t("checkout.submitInstallments", { count: breakdown.installments, value: money(breakdown.installmentCents) })
      : null;
  const submitLabel = installmentsPayLabel ?? t("checkout.submit", { amount: payAmountLabel });

  function toggleAllBumps(keys: string[], checked: boolean) {
    setSelectedBumpKeys((prev) => {
      const next = new Set(prev);
      for (const key of keys) {
        if (checked) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  }

  function toggleBump(key: string, checked: boolean) {
    setSelectedBumpKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }
  const offerKind: CheckoutOfferKind | null =
    breakdown?.offerKind && isCheckoutOfferKind(breakdown.offerKind) ? breakdown.offerKind : null;
  const offerLockedPlan = offerKind === "premium_at_basic";
  const showChangePlan = !urlOfferToken || (offerKind !== null && !offerLockedPlan);
  const backToPlansUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (interval === "annual") params.set("interval", "annual");
    if (email.trim()) params.set("email", normalizeEmail(email));
    if (fullName.trim()) params.set("name", fullName.trim());
    // Preserve the sales token + phone lock so the phone stays locked on return.
    if (urlSalesToken) params.set("sales_token", urlSalesToken);
    if (phoneLockedFromToken) params.set("phone_locked", "1");
    if (urlOfferToken) {
      params.set("offer", urlOfferToken);
      params.set("interval", "monthly");
    }
    if (urlBumps.length) params.set("bump", urlBumps.join(","));
    // Inside the demo iframe, in the buyer's language, with the campaign that brought them.
    copyNavigationContext(urlParams, params);
    const qs = params.toString();
    return qs ? `/plans?${qs}` : "/plans";
  }, [interval, email, fullName, urlSalesToken, phoneLockedFromToken, urlOfferToken, urlBumps]);

  return (
    <main className="min-h-screen surface-aurora pb-28 lg:pb-14">
      <div className="mx-auto max-w-5xl px-4 py-8 lg:py-14">
        {showChangePlan && (
          <button
            type="button"
            onClick={() => setLocation(backToPlansUrl)}
            className="inline-flex items-center gap-1.5 text-sm opacity-80 hover:opacity-100 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("checkout.changePlan")}
          </button>
        )}

        {offerInvalidReason && (
          <aside className="checkout-offer-banner mb-6" role="status">
            <p className="checkout-offer-banner-body">{t(offerFailureI18nKey(offerInvalidReason))}</p>
          </aside>
        )}

        {offerKind && (
          <CheckoutOfferBanner
            kind={offerKind}
            plan={plan}
            discountCents={Math.max(0, (breakdown?.originalCents ?? 0) - (breakdown?.finalCents ?? 0))}
            firstCents={breakdown?.finalCents ?? 0}
            recurringCents={breakdown?.recurringCents ?? breakdown?.finalCents ?? 0}
            addOnCents={bumpPaidCents}
            className="mb-6"
          />
        )}

        <div className="grid lg:grid-cols-[1fr_460px] gap-6 lg:gap-10 items-start">
          {/* ─── LEFT — order summary ─────────────────────────────────── */}
          <aside className="lg:sticky lg:top-8 order-2 lg:order-1">
            <OrderSummary
              plan={plan}
              interval={interval}
              // "12x" only when this purchase goes to Stone; on Stripe the year is one charge.
              breakdown={
                breakdown && !payWithStone
                  ? { ...breakdown, installments: undefined, installmentCents: undefined }
                  : breakdown
              }
              bumpLines={summaryBumpLines}
              totalCents={checkoutTotalCents}
              annualSavings={annualSavings}
              monthlyEquivalent={getPlanPricing(plan, "annual", currency).monthlyEquivalentCents}
              annualTotal={getPlanPricing(plan, "annual", currency).totalCents}
              onSwitchInterval={(v) => setIntervalValue(v)}
              offer={Boolean(offerKind)}
            />
          </aside>

          {/* ─── RIGHT — payment form ─────────────────────────────────── */}
          <section className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)] order-1 lg:order-2">
            <h2 className="text-xl font-semibold mb-5">{t("checkout.title")}</h2>

            {pixLocaleAvailable && (
              <div className="mb-5">
                <MethodTabs value={paymentMethod} onChange={setPaymentMethod} pixDisabled={!pixAvailable} />
                <p className="text-xs text-ink-2 mt-2">
                  {!pixAvailable
                    ? t("checkout.pixBrazilOnly")
                    : t(methodHintKey(paymentMethod, interval))}
                </p>
              </div>
            )}

            <form
              id="checkout-form"
              onSubmit={submit}
              // The browser's own bubble handles an empty or malformed field; this only records which.
              onInvalidCapture={(e) => {
                const name = nativeInvalidEventFor(e.target as Element, e.currentTarget);
                if (name) clarityEvent(name);
              }}
              className="space-y-4"
            >
              {activePixOrder &&
                isPixOrderActive(activePixOrder) &&
                pixOrderMatchesBumps(activePixOrder, selectedBumpKeys) && (
                  <ResumePixBanner
                    order={activePixOrder}
                    onExpired={() => setActivePixOrder(null)}
                    onResume={() => setLocation(`/pix/pending/${activePixOrder.id}`)}
                  />
                )}

              {phoneLockedFromToken && phoneField}

              <Field label={t("checkout.fullName")} error={fieldErrorMessage("fullName")}>
                <input
                  id={CHECKOUT_FIELD_IDS.fullName}
                  className="input-clean"
                  type="text"
                  autoComplete="name"
                  placeholder={t("checkout.fullNamePlaceholder")}
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    clearFieldError("fullName");
                  }}
                  required
                  minLength={2}
                />
              </Field>

              <Field label={t("checkout.email")} error={fieldErrorMessage("email")}>
                <input
                  id={CHECKOUT_FIELD_IDS.email}
                  className="input-clean"
                  type="email"
                  autoComplete="email"
                  placeholder={t("checkout.emailPlaceholder")}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value.toLowerCase());
                    setEmailAlreadyActive(false);
                    setEmailSuggestion(null);
                    clearFieldError("email");
                    if (error) setError(null);
                  }}
                  onBlur={() => {
                    const normalized = normalizeEmail(email);
                    if (normalized !== email) setEmail(normalized);
                    setEmailSuggestion(suggestEmailFix(normalized));
                  }}
                  required
                  {...localizedInputValidationProps(t, { email: true })}
                />
                {emailSuggestion && (
                  <p
                    className={`mt-1.5 text-xs ${fieldError?.code === "email_typo" ? "text-danger" : "text-ink-2"}`}
                    role={fieldError?.code === "email_typo" ? "alert" : undefined}
                  >
                    {t("checkout.emailTypoHint")}{" "}
                    <button
                      type="button"
                      className="font-medium text-purple-deep underline underline-offset-2"
                      onClick={() => {
                        setEmail(emailSuggestion);
                        setEmailSuggestion(null);
                        clearFieldError("email");
                        if (error) setError(null);
                      }}
                    >
                      {emailSuggestion}
                    </button>
                  </p>
                )}
              </Field>

              {!phoneLockedFromToken && phoneField}

              {needsCpf && (
                <Field label={t("checkout.cpf")} error={fieldErrorMessage("cpf")}>
                  <CpfInput
                    id={CHECKOUT_FIELD_IDS.cpf}
                    placeholder={t("checkout.cpfPlaceholder")}
                    value={cpf}
                    onChange={(next) => {
                      setCpf(next);
                      clearFieldError("cpf");
                    }}
                    required
                  />
                </Field>
              )}

              {payWithStone && (
                <StoneCardForm
                  value={stoneCard}
                  onChange={setStoneCard}
                  installmentsLabel={stoneInstallmentsLabel}
                  disabled={submitting}
                />
              )}

              {/* The Stone card is charged on this screen, so the terms the Stripe and PIX screens show go here. */}
              {payWithStone && checkoutTotalCents != null && (
                <RenewalTerms
                  todayCents={checkoutTotalCents}
                  renewalCents={
                    offerKind && breakdown?.recurringCents != null
                      ? breakdown.recurringCents + bumpPaidCents
                      : checkoutTotalCents
                  }
                  interval={interval}
                  installments={
                    breakdown?.installments && breakdown.installmentCents
                      ? { count: breakdown.installments, cents: breakdown.installmentCents }
                      : null
                  }
                />
              )}

              {bumpsLoading && bumpItems.length === 0 ? (
                <OrderBumpSectionLoading />
              ) : bumpItems.length > 0 ? (
                <OrderBumpSection
                  items={bumpItems}
                  interval={interval}
                  locale={i18n.language}
                  currency={currency}
                  selectedKeys={selectedBumpKeys}
                  onToggle={toggleBump}
                  onToggleAll={toggleAllBumps}
                  // Same condition the server uses to split an annual card sale into 12 on Stone.
                  installments={
                    stonePublicKey && paymentMethod === "stripe" && interval === "annual" ? STONE_INSTALLMENT_COUNT : undefined
                  }
                />
              ) : null}

              {!urlOfferToken && (
              <CouponSection
                open={couponOpen}
                draft={couponDraft}
                applied={couponApplied}
                discountCents={breakdown?.discountCents ?? 0}
                error={couponError}
                onToggle={() => setCouponOpen((v) => !v)}
                onChangeDraft={setCouponDraft}
                onApply={applyCoupon}
                onRemove={removeCoupon}
              />
              )}

              {emailAlreadyActive && (
                <div ref={formErrorRef} className="text-sm text-danger space-y-2" role="alert">
                  <p>{t("checkout.errors.emailAlreadyActive")}</p>
                  <a
                    href={ZENPOST_APP_SUBSCRIPTION_URL}
                    className="inline-flex font-medium underline underline-offset-2 hover:opacity-90"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("checkout.errors.goToApp")}
                  </a>
                </div>
              )}
              {error && (
                <div ref={formErrorRef} className="text-sm text-danger space-y-1" role="alert">
                  <p>{error}</p>
                  {pixUnavailable && paymentMethod === "openpix" && (
                    <button
                      type="button"
                      className="btn-ghost text-sm px-3 py-1.5 mt-1"
                      onClick={() => {
                        setPaymentMethod("stripe");
                        setError(null);
                        setPixUnavailable(false);
                      }}
                    >
                      {t("checkout.pixUnavailableCta")}
                    </button>
                  )}
                  {import.meta.env.DEV && errorDetail && (
                    <p className="text-xs text-ink-2 font-mono break-all">{errorDetail}</p>
                  )}
                </div>
              )}

              <button type="submit" className="btn-primary w-full mt-2 hidden lg:flex" disabled={submitting || !breakdown}>
                {submitting ? t("checkout.submitProcessing") : submitLabel}
              </button>

              <p className="text-xs text-center text-ink-2 flex items-center justify-center gap-1.5 pt-1">
                <Lock className="w-3 h-3" />
                {payWithStone
                  ? t("checkout.secureNoteStone")
                  : paymentMethod === "stripe"
                    ? t("checkout.secureNoteStripe")
                    : t("checkout.secureNoteOpenpix")}
              </p>

              {/* ─── Terms checkbox temporarily disabled by request ───────
                  Restore by uncommenting this block + re-adding
                  `acceptTerms` state and the `errors.terms` validation
                  above. The backend already enforces accept_terms: true.

              <label className="flex items-start gap-3 cursor-pointer text-xs text-ink-2 mt-3">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  <Trans
                    i18nKey="checkout.termsAccept"
                    components={{
                      terms: <a className="underline" href={TERMS_URL} target="_blank" rel="noreferrer" />,
                      refund: <a className="underline" href={REFUND_URL} target="_blank" rel="noreferrer" />,
                    }}
                  />
                </span>
              </label>
              */}
            </form>
          </section>
        </div>
      </div>

      <PaymentStickyBar
        amountLabel={t("checkout.billingNow")}
        amount={payAmountLabel}
        buttonLabel={submitting ? t("checkout.submitProcessing") : submitLabel}
        disabled={submitting || !breakdown}
        formId="checkout-form"
        note={
          installmentsPayLabel && breakdown?.installments && breakdown.installmentCents
            ? t("checkout.stickyInstallments", {
                installments: t("plans.annualInstallments", {
                  count: breakdown.installments,
                  value: money(breakdown.installmentCents),
                }),
                total: payAmountLabel,
              })
            : `${t("checkout.billingNow")}: ${payAmountLabel}${
                // An offer's first month is discounted, so today's amount is not what renews.
                offerKind ? "" : t(interval === "annual" ? "plans.perYear" : "plans.perMonth")
              }`
        }
      />
    </main>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function Field({
  label,
  badge,
  error,
  children,
}: {
  label: string;
  badge?: React.ReactNode;
  /** zenpay's refusal for this field, shown right under it. */
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="field-label flex items-center gap-2">
        <span>{label}</span>
        {badge}
      </span>
      {children}
      {error && (
        <p className="mt-1.5 text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </label>
  );
}

function MethodTabs({
  value,
  onChange,
  pixDisabled,
}: {
  value: PaymentMethod;
  onChange: (v: PaymentMethod) => void;
  pixDisabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="method-tabs" role="tablist" aria-label="payment method">
      <button
        type="button"
        role="tab"
        aria-selected={value === "openpix"}
        data-active={value === "openpix"}
        className="method-tab"
        disabled={pixDisabled}
        title={pixDisabled ? t("checkout.pixBrazilOnly") : undefined}
        onClick={() => {
          if (!pixDisabled) onChange("openpix");
        }}
      >
        <Zap className="w-4 h-4" />
        <span>{t("checkout.pixLong")}</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === "stripe"}
        data-active={value === "stripe"}
        className="method-tab"
        onClick={() => onChange("stripe")}
      >
        <CreditCard className="w-4 h-4" />
        <span>{t("checkout.cardLong")}</span>
      </button>
    </div>
  );
}

function OrderSummary({
  plan,
  interval,
  breakdown,
  bumpLines,
  totalCents,
  annualSavings,
  monthlyEquivalent,
  annualTotal,
  onSwitchInterval,
  offer,
}: {
  plan: Plan;
  interval: BillingInterval;
  breakdown: PricingBreakdown | null;
  bumpLines: { key: string; headline: string; list_cents: number; charged_cents: number; source: string }[];
  totalCents: number | null;
  annualSavings: { savingsCents: number; percentage: number };
  monthlyEquivalent: number;
  annualTotal: number;
  onSwitchInterval: (v: BillingInterval) => void;
  offer?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const currency = currencyFromLocale(i18n.language);
  const money = (cents: number) => formatMoney(cents, i18n.language, currency);
  const planLabel = t(`plans.${plan}`);
  const intervalLabel = t(`plans.${interval}`).toLowerCase();
  const headlineCents = totalCents ?? breakdown?.finalCents ?? 0;
  // Paid add-ons in the headline. On an offer they are not discounted, so the struck price and the
  // amount from month 2 carry them too.
  const addOnCents = breakdown ? Math.max(0, headlineCents - breakdown.finalCents) : 0;
  const installments =
    !offer && breakdown?.installments && breakdown.installmentCents
      ? { count: breakdown.installments, cents: breakdown.installmentCents }
      : null;

  return (
    <div className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)] space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-ink-2 mb-1.5">{t("checkout.summary")}</div>
        <div className="text-base font-medium text-ink-2">
          Zenpost {planLabel} · {intervalLabel}
        </div>
        {installments ? (
          // Annual on the card: the instalment is what the buyer compares, so it leads.
          <>
            <div className="mt-1 flex items-baseline gap-1.5 flex-wrap">
              <span className="text-xl font-semibold">{installments.count}x</span>
              <span className="text-4xl font-semibold" style={{ letterSpacing: "-0.02em" }}>
                {money(installments.cents)}
              </span>
              <span className="text-sm text-ink-2">{t("checkout.installmentsInterestFree")}</span>
            </div>
            <div className="text-sm text-ink-2 mt-1">
              {t("checkout.installmentsTotal", { total: money(headlineCents) })}
            </div>
          </>
        ) : (
          <>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              {offer && breakdown?.originalCents != null && breakdown.originalCents + addOnCents > headlineCents && (
                <span className="text-xl text-ink-2 line-through opacity-55">
                  {money(breakdown.originalCents + addOnCents)}
                </span>
              )}
              <span className="text-4xl font-semibold" style={{ letterSpacing: "-0.02em" }}>
                {money(headlineCents)}
              </span>
              <span className="text-sm text-ink-2">
                {offer ? t("checkout.offerToday") : interval === "monthly" ? t("plans.perMonth") : t("plans.perYear")}
              </span>
            </div>
            {offer && breakdown?.recurringCents != null && (
              <div className="text-sm text-ink-2 mt-2 space-y-1">
                <div>{t("checkout.offerThen", { amount: money(breakdown.recurringCents + addOnCents) })}</div>
              </div>
            )}
            {interval === "annual" && (
              <div className="text-xs text-ink-2 mt-1">
                {t("plans.monthlyEquivalent", { value: money(monthlyEquivalent) })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Annual upsell — only when on monthly, and only when the savings are non-zero. */}
      {interval === "monthly" && annualSavings.savingsCents > 0 && !offer && (
        <div className="upsell">
          <div>
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold">{t("checkout.upsellTitleShort")}</div>
              <span className="save-pct-badge shrink-0" aria-hidden="true">
                -{annualSavings.percentage}%
              </span>
            </div>
            <div className="text-xs text-ink-2 mt-1">
              {t("checkout.upsellBody", {
                amount: money(annualTotal),
                monthly: money(monthlyEquivalent),
              })}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onSwitchInterval("annual")}
            className="btn-ghost text-sm w-full justify-center"
            style={{ padding: "10px 16px" }}
          >
            {t("checkout.switchToAnnual")} →
          </button>
        </div>
      )}

      {interval === "annual" && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-success font-medium">
            ✓ {t("checkout.savingMessage", { amount: money(annualSavings.savingsCents) })}
          </span>
          <button type="button" onClick={() => onSwitchInterval("monthly")} className="text-ink-2 hover:text-text-primary underline">
            {t("checkout.switchToMonthly")}
          </button>
        </div>
      )}

      {breakdown && (
        <div className="border-t border-black/10 pt-4 space-y-1.5 text-sm">
          <Row label={t("checkout.originalPrice")} value={money(breakdown.originalCents)} />
          {breakdown.discountCents > 0 && (
            <Row label={t("checkout.discount")} value={`− ${money(breakdown.discountCents)}`} accent="success" />
          )}
          {bumpLines.map((line) => (
            <Row
              key={line.key}
              label={
                // The combo line is the only paid one charged below its list price.
                line.charged_cents > 0 && line.list_cents > line.charged_cents ? (
                  <>
                    {line.headline}{" "}
                    <span className="ml-1 rounded-full bg-success px-1.5 py-0.5 text-[11px] font-bold text-white whitespace-nowrap">
                      {t("checkout.bumps.discountBadge", {
                        percent: Math.round((1 - line.charged_cents / line.list_cents) * 100),
                      })}
                    </span>
                  </>
                ) : (
                  line.headline
                )
              }
              value={
                line.charged_cents > 0 ? (
                  line.list_cents > line.charged_cents ? (
                    <>
                      <span className="line-through text-ink-2 mr-1.5">{money(line.list_cents)}</span>
                      <span className="font-semibold text-success">+ {money(line.charged_cents)}</span>
                    </>
                  ) : (
                    `+ ${money(line.charged_cents)}`
                  )
                ) : (
                  <>
                    <span className="line-through text-ink-2 mr-1.5">{money(line.list_cents)}</span>
                    <span className="font-semibold text-success">{t("checkout.bumps.free")}</span>
                  </>
                )
              }
            />
          ))}
        </div>
      )}

      <div className="border-t border-black/10 pt-3 flex items-baseline justify-between gap-4">
        <span className="text-sm text-ink-2">{t("checkout.billingNow")}</span>
        {installments ? (
          // On the card's 12 instalments only the first one is charged today.
          <span className="text-right">
            <span className="block text-2xl font-semibold">{money(installments.cents)}</span>
            <span className="block text-xs text-ink-2">
              {t("checkout.firstInstallment", { count: installments.count })}
            </span>
          </span>
        ) : (
          <span className="text-2xl font-semibold">{money(headlineCents)}</span>
        )}
      </div>

      <div className="text-xs text-ink-2 border-t border-black/10 pt-3 leading-relaxed">
        {t("plans.guarantee")}
      </div>
    </div>
  );
}

function Row({ label, value, accent }: { label: React.ReactNode; value: React.ReactNode; accent?: "success" }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-ink-2 min-w-0">{label}</span>
      <span className={`shrink-0 text-right whitespace-nowrap ${accent === "success" ? "text-success font-medium" : ""}`}>
        {value}
      </span>
    </div>
  );
}

/**
 * Returns the i18n key for the small explainer that sits below the
 * payment-method tabs. The four variants reflect the actual provider behaviour:
 *   - card monthly  -> Stripe Subscription (auto-renews monthly)
 *   - card annual   -> Stripe Subscription (auto-renews yearly)
 *   - pix monthly   -> OpenPix PIX Automático (auto-charged every month)
 *   - pix annual    -> OpenPix PIX Automático (auto-charged every year)
 * See server/providers/{stripe,openpix}.ts for the source of truth.
 */
function methodHintKey(method: PaymentMethod, interval: BillingInterval): string {
  if (method === "stripe") {
    return interval === "monthly" ? "checkout.cardHintMonthly" : "checkout.cardHintAnnual";
  }
  return interval === "monthly" ? "checkout.pixHintMonthly" : "checkout.pixHintAnnual";
}
